/**
 * Multi-provider ICP test import form.
 *
 * Collapsible panel with two tabs:
 * - From link: paste a results link or barcode; the provider is auto-detected
 *   (Reef Zelements / ATI / Triton) with a manual fallback selector.
 * - From file: upload an ATI PDF report or Triton CSV export. When the file
 *   carries no metadata (renamed download), a confirmation step collects it.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  FileButton,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconFileUpload,
  IconLink,
  IconUpload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useICPImportDefaults,
  useImportICPTest,
  useImportICPTestFile,
} from '@/hooks/useICPTest';
import { useTank } from '@/contexts/TankContext';
import {
  detectICPSource,
  PROVIDER_LABELS,
  type ICPProviderId,
} from '@/utils/icpProviderDetect';
import { ICPFileImportMissingFieldsError } from '@/api/icpTests';
import type {
  ICPFileImportMissingFieldsDetail,
  ICPFileImportOverrides,
  ICPTestImport,
} from '@/types/sensor';
import { ImportFileConfirm, ProviderBadge } from './ICP';

const PROVIDER_OPTIONS = (Object.keys(PROVIDER_LABELS) as ICPProviderId[]).map(
  (provider) => ({ value: provider, label: PROVIDER_LABELS[provider] })
);

export function ICPImportForm() {
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('link');

  // From link state
  const [source, setSource] = useState('');
  const [manualProvider, setManualProvider] = useState<ICPProviderId | null>(null);
  // null = untouched -> fall back to the remembered provider label
  const [tankNameInput, setTankNameInput] = useState<string | null>(null);

  // From file state
  const [file, setFile] = useState<File | null>(null);
  const [confirmDetail, setConfirmDetail] = useState<ICPFileImportMissingFieldsDetail | null>(null);

  const { selectedTank } = useTank();
  const importMutation = useImportICPTest();
  const fileMutation = useImportICPTestFile();
  const defaultsQuery = useICPImportDefaults(selectedTank?.id);

  const detection = useMemo(() => detectICPSource(source), [source]);
  const hasInput = source.trim().length > 0;
  const showProviderPicker = hasInput && !detection;
  const effectiveProvider: ICPProviderId | null =
    detection?.provider ?? (showProviderPicker ? manualProvider : null);

  // RZ tank-name field: hidden entirely when the pasted URL already carries
  // tank_name; otherwise shown, prefilled from the remembered provider label.
  const tankNameFromUrl = detection?.provider === 'reef_zelements' && !!detection.tankName;
  const showTankField = effectiveProvider === 'reef_zelements' && !tankNameFromUrl;
  const rememberedTankLabel = defaultsQuery.data?.provider_labels?.reef_zelements ?? '';
  const tankName = tankNameInput ?? rememberedTankLabel;

  // ATI/Triton can't be imported from a link yet (Phase 3) — point at files.
  const linkBlocked = effectiveProvider === 'ati' || effectiveProvider === 'triton';

  const resetForm = () => {
    setSource('');
    setManualProvider(null);
    setTankNameInput(null);
    setFile(null);
    setConfirmDetail(null);
    importMutation.reset();
    fileMutation.reset();
  };

  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedSource = source.trim();
    if (!trimmedSource) {
      notifications.show({
        title: 'Validation Error',
        message: 'Paste a results link or barcode',
        color: 'red',
      });
      return;
    }
    if (showProviderPicker && !manualProvider) {
      notifications.show({
        title: 'Validation Error',
        message: 'Select the ICP provider for this link or barcode',
        color: 'red',
      });
      return;
    }

    const payload: ICPTestImport = { source: trimmedSource };
    if (!detection && manualProvider) {
      payload.provider = manualProvider;
    }
    if (showTankField && tankName.trim()) {
      payload.tank_name = tankName.trim();
    }

    importMutation.mutate(payload, {
      onSuccess: (data) => {
        notifications.show({
          title: 'Success',
          message: `ICP test ${data.barcode} imported successfully`,
          color: 'green',
        });
        resetForm();
        setOpened(false);
      },
      onError: (error) => {
        notifications.show({
          title: 'Import Failed',
          message: error instanceof Error ? error.message : 'Failed to import ICP test',
          color: 'red',
        });
      },
    });
  };

  const handleFileSubmit = (overrides?: ICPFileImportOverrides) => {
    if (!file) {
      notifications.show({
        title: 'Validation Error',
        message: 'Choose an ATI PDF report or Triton CSV export first',
        color: 'red',
      });
      return;
    }

    fileMutation.mutate(
      { file, overrides },
      {
        onSuccess: (data) => {
          notifications.show({
            title: 'Success',
            message: `ICP test ${data.barcode} imported successfully`,
            color: 'green',
          });
          resetForm();
          setOpened(false);
        },
        onError: (error) => {
          if (error instanceof ICPFileImportMissingFieldsError) {
            // Missing metadata is a confirmation step, not a failure — but only
            // if we still have the file to resubmit (a concurrent reset in the
            // other tab can clear it out from under a late-arriving 422).
            if (file) {
              setConfirmDetail(error.detail);
            }
            return;
          }
          notifications.show({
            title: 'Import Failed',
            message: error instanceof Error ? error.message : 'Failed to import ICP test',
            color: 'red',
          });
        },
      }
    );
  };

  const handleFileSelect = (selected: File | null) => {
    setFile(selected);
    setConfirmDetail(null);
    fileMutation.reset();
  };

  const fileError =
    fileMutation.isError && !(fileMutation.error instanceof ICPFileImportMissingFieldsError)
      ? fileMutation.error
      : null;

  return (
    <Paper p="md" withBorder>
      {/* Header - always visible */}
      <Group justify="space-between" onClick={() => setOpened(!opened)} style={{ cursor: 'pointer' }}>
        <Text fw={600} size="lg">Add New ICP Test</Text>
        {opened ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
      </Group>

      {/* Form - collapsible */}
      <Collapse in={opened}>
        <Tabs value={activeTab} onChange={setActiveTab} mt="md">
          <Tabs.List>
            <Tabs.Tab value="link" leftSection={<IconLink size={16} />}>
              From link
            </Tabs.Tab>
            <Tabs.Tab value="file" leftSection={<IconFileUpload size={16} />}>
              From file
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="link" pt="md">
            <form onSubmit={handleLinkSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Results link or barcode"
                  placeholder="Paste a Reef Zelements, ATI, or Triton results link or barcode"
                  value={source}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setSource(value);
                    // Detectable input supersedes any earlier manual choice, so
                    // a stale selection can't reappear if the user edits back to
                    // undetectable text.
                    if (detectICPSource(value)) {
                      setManualProvider(null);
                    }
                  }}
                  required
                />

                {detection && (
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      ✓ Detected:
                    </Text>
                    <ProviderBadge provider={detection.provider} size="sm" />
                  </Group>
                )}

                {showProviderPicker && (
                  <Stack gap={4}>
                    <Text size="sm" c="dimmed">
                      We couldn't detect the provider — choose one:
                    </Text>
                    <SegmentedControl
                      fullWidth
                      size="xs"
                      value={manualProvider ?? ''}
                      onChange={(value) => setManualProvider(value as ICPProviderId)}
                      data={PROVIDER_OPTIONS}
                    />
                  </Stack>
                )}

                {showTankField && (
                  <TextInput
                    label="Tank name on report"
                    placeholder="Tank name used on the Reef Zelements report"
                    value={tankName}
                    onChange={(e) => setTankNameInput(e.currentTarget.value)}
                  />
                )}

                {linkBlocked && effectiveProvider && (
                  <Alert icon={<IconAlertCircle size={16} />} color="teal" variant="light">
                    <Stack gap="xs" align="flex-start">
                      <Text size="sm">
                        {PROVIDER_LABELS[effectiveProvider]} link import is coming soon —
                        upload the {effectiveProvider === 'ati' ? 'PDF report' : 'CSV export'}{' '}
                        instead.
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => setActiveTab('file')}
                      >
                        Upload a file instead
                      </Button>
                    </Stack>
                  </Alert>
                )}

                {importMutation.isError && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    {importMutation.error instanceof Error
                      ? importMutation.error.message
                      : 'Failed to import test'}
                  </Alert>
                )}

                <Button
                  type="submit"
                  loading={importMutation.isPending}
                  disabled={linkBlocked}
                  fullWidth
                >
                  Retrieve Test Results
                </Button>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="file" pt="md">
            {confirmDetail ? (
              <ImportFileConfirm
                detail={confirmDetail}
                submitting={fileMutation.isPending}
                errorMessage={fileError?.message}
                onConfirm={(overrides) => handleFileSubmit(overrides)}
                onCancel={() => setConfirmDetail(null)}
              />
            ) : (
              <Stack gap="md">
                <Group gap="sm">
                  <FileButton onChange={handleFileSelect} accept=".pdf,.csv">
                    {(props) => (
                      <Button
                        {...props}
                        variant="outline"
                        leftSection={<IconUpload size={16} />}
                      >
                        Choose file
                      </Button>
                    )}
                  </FileButton>
                  {file ? (
                    <Text size="sm">{file.name}</Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      ATI PDF report or Triton CSV export
                    </Text>
                  )}
                </Group>

                {fileError && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    {fileError.message}
                  </Alert>
                )}

                <Button
                  onClick={() => handleFileSubmit()}
                  loading={fileMutation.isPending}
                  disabled={!file}
                  fullWidth
                >
                  Import File
                </Button>
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Collapse>
    </Paper>
  );
}
